import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { 
  Calendar, Moon, Sun, Leaf, Snowflake, Flame, Star, ChefHat,
  ArrowRight, Heart, Trophy, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';

const SEASON_ICONS = {
  ramadan: Moon,
  aid_el_fitr: Star,
  aid_el_adha: Heart,
  harissa_season: Flame,
  olive_season: Leaf,
  summer: Sun,
  winter: Snowflake,
  mouled: Moon,
};

const SEASON_COLORS = {
  ramadan: 'from-purple-500 to-indigo-600',
  aid_el_fitr: 'from-[#F2A900] to-[#E07A5F]',
  aid_el_adha: 'from-[#D9381E] to-[#E07A5F]',
  harissa_season: 'from-[#D9381E] to-red-600',
  olive_season: 'from-[#4F6F52] to-green-600',
  summer: 'from-[#F2A900] to-orange-500',
  winter: 'from-[#1E4ED8] to-blue-400',
  mouled: 'from-purple-400 to-pink-500',
};

export default function CulturalSeasonsPage() {
  const { t } = useTranslation();
  const [seasons, setSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSeasons();
  }, []);

  const fetchSeasons = async () => {
    try {
      const response = await api.get('/cultural/seasons');
      setSeasons(response.data.seasons || []);
      setCurrentSeason(response.data.current_season);
    } catch (error) {
      console.error('Error fetching seasons:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#1E4ED8] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F5F2]" data-testid="cultural-seasons-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1E4ED8] to-[#0D3A9E] py-16 px-4 relative overflow-hidden">
        {/* Decorative moon/stars */}
        <div className="absolute top-10 right-20 text-white/20">
          <Moon className="w-24 h-24" />
        </div>
        <div className="absolute bottom-10 left-10 text-white/10">
          <Star className="w-16 h-16" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <Calendar className="w-5 h-5 text-white" />
            <span className="text-white font-medium">{t('seasons.calendarTitle', 'Tunisian Cultural Calendar')}</span>
          </div>
          
          <h1 className="font-fraunces text-4xl md:text-5xl font-bold text-white mb-4">
            {t('seasons.culinarySeasons', 'Culinary Seasons')}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            {t('seasons.heroSubtitle', 'MHARHER follows the Tunisian cultural calendar. Discover the challenges and recipes of each season.')}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Season Highlight */}
        {currentSeason && (
          <div className={`bg-gradient-to-r ${SEASON_COLORS[currentSeason.season] || 'from-[#1E4ED8] to-[#60A5FA]'} rounded-2xl p-8 mb-12 text-white relative overflow-hidden`}>
            <div className="absolute top-0 right-0 opacity-20">
              {SEASON_ICONS[currentSeason.season] && 
                React.createElement(SEASON_ICONS[currentSeason.season], { className: "w-48 h-48 -mt-10 -mr-10" })
              }
            </div>
            
            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-sm mb-4">
                <Clock className="w-4 h-4" />
                {t('seasons.ongoing', 'Ongoing season')}
              </span>
              
              <h2 className="font-fraunces text-3xl font-bold mb-2">{currentSeason.name}</h2>
              <p className="text-lg text-white/90 font-arabic mb-2">{currentSeason.name_ar}</p>
              <p className="text-white/80 max-w-xl mb-6">{currentSeason.description}</p>
              
              {/* Traditional Dishes */}
              <div className="flex flex-wrap gap-2 mb-6">
                {currentSeason.traditional_dishes?.slice(0, 5).map((dish, i) => (
                  <span key={i} className="px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium">
                    {dish}
                  </span>
                ))}
              </div>
              
              {currentSeason.active_challenge ? (
                <Link to={`/challenges/${currentSeason.active_challenge.id}`}>
                  <Button className="bg-white text-gray-900 hover:bg-white/90">
                    <Trophy className="w-4 h-4 mr-2" />
                    {t('seasons.participateChallenge', 'Join the Challenge')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <Button className="bg-white/20 hover:bg-white/30">
                  <ChefHat className="w-4 h-4 mr-2" />
                  {t('seasons.exploreRecipes', 'Explore recipes')}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* All Seasons Grid */}
        <h2 className="font-fraunces text-2xl font-bold text-gray-900 mb-6">
          {t('seasons.allSeasons', 'All Seasons')}
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {seasons.map((season) => {
            const Icon = SEASON_ICONS[season.season] || Calendar;
            const colorClass = SEASON_COLORS[season.season] || 'from-gray-500 to-gray-600';
            const isActive = currentSeason?.id === season.id;
            
            return (
              <div 
                key={season.id}
                className={`bg-white rounded-2xl border overflow-hidden transition-all hover:shadow-lg ${
                  isActive ? 'border-[#1E4ED8] ring-2 ring-[#1E4ED8]/20' : 'border-gray-100'
                }`}
              >
                {/* Season Header */}
                <div className={`bg-gradient-to-r ${colorClass} p-6 text-white relative`}>
                  <div className="absolute top-2 right-2 opacity-30">
                    <Icon className="w-16 h-16" />
                  </div>
                  <div className="relative z-10">
                    {isActive && (
                      <span className="inline-block px-2 py-0.5 bg-white/30 rounded-full text-xs font-medium mb-2">
                        {t('seasons.ongoing', 'Ongoing')}
                      </span>
                    )}
                    <h3 className="font-fraunces text-xl font-bold">{season.name}</h3>
                    <p className="text-sm text-white/80 font-arabic">{season.name_ar}</p>
                  </div>
                </div>
                
                {/* Season Content */}
                <div className="p-6 space-y-4">
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {season.description}
                  </p>
                  
                  {/* Period */}
                  {!season.is_lunar_calendar && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {season.start_month && `${t('seasons.month', 'Month')} ${season.start_month}`}
                        {season.end_month && season.end_month !== season.start_month && ` - ${season.end_month}`}
                      </span>
                    </div>
                  )}
                  {season.is_lunar_calendar && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Moon className="w-4 h-4" />
                      <span>{t('seasons.lunarCalendar', 'Lunar calendar')}</span>
                    </div>
                  )}
                  
                  {/* Traditional Dishes */}
                  <div className="flex flex-wrap gap-1">
                    {season.traditional_dishes?.slice(0, 3).map((dish, i) => (
                      <span 
                        key={i}
                        className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs"
                      >
                        {dish}
                      </span>
                    ))}
                    {season.traditional_dishes?.length > 3 && (
                      <span className="px-2 py-0.5 text-gray-400 text-xs">
                        +{season.traditional_dishes.length - 3}
                      </span>
                    )}
                  </div>
                  
                  {/* Challenge Status */}
                  {season.active_challenge ? (
                    <Link 
                      to={`/challenges/${season.active_challenge.id}`}
                      className="flex items-center justify-between p-3 bg-[#F2A900]/10 rounded-lg hover:bg-[#F2A900]/20 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-[#F2A900]" />
                        <span className="text-sm font-medium text-gray-900">{t('seasons.challengeActive', 'Active challenge!')}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#F2A900]" />
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg text-gray-500 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>{t('seasons.challengeUpcoming', 'Challenge coming')}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Cultural Info */}
        <div className="mt-12 bg-white rounded-2xl border border-gray-100 p-8">
          <h3 className="font-fraunces text-xl font-bold text-gray-900 mb-4">
            🇹🇳 {t('seasons.cultureInfo.title', 'The Tunisian Culinary Calendar')}
          </h3>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600">
              {t('seasons.cultureInfo.intro', 'Tunisian cuisine is intimately linked to seasons and religious and cultural celebrations. Each period of the year brings its own culinary traditions, seasonal ingredients, and iconic dishes.')}
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">🌙 {t('seasons.cultureInfo.ramadan.title', 'Ramadan')}</h4>
                <p className="text-sm text-purple-700">
                  {t('seasons.cultureInfo.ramadan.description', 'The holy month is marked by rich soups (chorba), crispy bricks, and generous pastries like zlabiya.')}
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <h4 className="font-semibold text-red-900 mb-2">🌶️ {t('seasons.cultureInfo.harissa.title', 'Harissa Season')}</h4>
                <p className="text-sm text-red-700">
                  {t('seasons.cultureInfo.harissa.description', 'September-October: Families gather to prepare homemade harissa, a moment of intergenerational sharing.')}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2">🫒 {t('seasons.cultureInfo.olive.title', 'Olive Season')}</h4>
                <p className="text-sm text-green-700">
                  {t('seasons.cultureInfo.olive.description', 'November-January: The olive harvest and new oil production, celebrated throughout the country.')}
                </p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <h4 className="font-semibold text-orange-900 mb-2">☀️ {t('seasons.cultureInfo.summer.title', 'Tunisian Summer')}</h4>
                <p className="text-sm text-orange-700">
                  {t('seasons.cultureInfo.summer.description', 'Fresh salads, grilled fish, and the famous slata mechouia accompany the long summer evenings.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
